import React from 'react';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ReactSelect from 'react-select';
import { FaGlobe, FaUniversity, FaBook, FaClock } from "react-icons/fa";

const OpportunityFilters = ({
  selectedCountry,
  selectedUniversity,
  selectedDiscipline,
  startDate,
  endDate,
  showFullyFundedOnly,
  countries,
  universities,
  disciplines,
  handleCountryChange,
  handleUniversityChange,
  handleDisciplineChange,
  handleStartDateChange,
  handleEndDateChange,
  handleCheckboxChange,
  handleClearFilters,
  setIsSidebarOpen,
  setShowFullyFundedOnly
}) => {
  return (
    <div className="h-full flex flex-col">
      {/* Mobile Close Button */}
      <div className="lg:hidden flex justify-between items-center p-4 border-b border-gray-200">
        <h2 className="text-sm font-semibold text-gray-900">Filters</h2>
        <button
          onClick={() => setIsSidebarOpen(false)}
          className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors rounded-lg hover:bg-gray-100"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Filters Content */}
      <div className="flex-1 p-4 space-y-3">
        {/* Country Selector */}
        <div className="space-y-1.5">
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide flex items-center">
            <FaGlobe className="text-[#DB4F40] mr-1.5 w-3 h-3" />
            Country
          </h3>

          <ReactSelect
            options={[
              { value: '', label: 'All Countries' },
              ...countries.map(country => ({
                value: country.country,
                label: country.country
              }))
            ]}
            value={{
              value: selectedCountry,
              label: selectedCountry || 'All Countries'
            }}
            onChange={(selectedOption) => handleCountryChange({
              target: { value: selectedOption.value }
            })}
            isSearchable
            isDisabled={!!selectedDiscipline}
            placeholder={selectedDiscipline ? "Disabled when discipline is selected" : "Search countries..."}
            className="react-select-container"
            classNamePrefix="react-select"
            styles={{
              control: (base) => ({
                ...base,
                border: '1px solid #E5E7EB',
                borderRadius: '6px',
                padding: '2px 6px',
                minHeight: '36px',
                fontSize: '14px',
                '&:hover': { borderColor: '#DB4F40' },
                '&:focus-within': {
                  borderColor: '#DB4F40',
                  boxShadow: '0 0 0 3px rgba(219, 79, 64, 0.1)'
                },
                opacity: selectedDiscipline ? 0.6 : 1
              }),
              option: (base, { isFocused }) => ({
                ...base,
                backgroundColor: isFocused ? '#FEF2F2' : 'white',
                color: '#374151',
                fontSize: '14px',
                padding: '6px 8px',
                '&:hover': { backgroundColor: '#FEF2F2' }
              }),
              menu: (base) => ({
                ...base,
                borderRadius: '6px',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
              }),
              placeholder: (base) => ({
                ...base,
                fontSize: '14px',
                color: '#9CA3AF'
              }),
              singleValue: (base) => ({
                ...base,
                fontSize: '14px'
              }),
              input: (base) => ({
                ...base,
                fontSize: '14px'
              })
            }}
          />
        </div>

        {/* University Selector */}
        <div className="space-y-1.5">
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide flex items-center">
            <FaUniversity className="text-[#DB4F40] mr-1.5 w-3 h-3" />
            University
          </h3>

          <ReactSelect
            options={[
              { value: '', label: 'All Universities' },
              ...universities.map(university => ({
                value: university.university,
                label: university.university
              }))
            ]}
            value={{
              value: selectedUniversity,
              label: selectedUniversity || 'All Universities'
            }}
            onChange={(selectedOption) => handleUniversityChange({
              target: { value: selectedOption.value }
            })}
            isSearchable
            isDisabled={!selectedCountry}
            placeholder={selectedCountry ? "Search universities..." : "Select a country first"}
            className="react-select-container"
            classNamePrefix="react-select"
            styles={{
              control: (base) => ({
                ...base,
                border: '1px solid #E5E7EB',
                borderRadius: '6px',
                padding: '2px 6px',
                minHeight: '36px',
                fontSize: '14px',
                '&:hover': { borderColor: '#DB4F40' },
                '&:focus-within': {
                  borderColor: '#DB4F40',
                  boxShadow: '0 0 0 3px rgba(219, 79, 64, 0.1)'
                },
                opacity: !selectedCountry ? 0.6 : 1
              }),
              option: (base, { isFocused }) => ({
                ...base,
                backgroundColor: isFocused ? '#FEF2F2' : 'white',
                color: '#374151',
                fontSize: '14px',
                padding: '6px 8px',
                '&:hover': { backgroundColor: '#FEF2F2' }
              }),
              menu: (base) => ({
                ...base,
                borderRadius: '6px',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
              }),
              placeholder: (base) => ({
                ...base,
                fontSize: '14px',
                color: '#9CA3AF'
              }),
              singleValue: (base) => ({
                ...base,
                fontSize: '14px'
              }),
              input: (base) => ({
                ...base,
                fontSize: '14px'
              })
            }}
          />
          {!selectedCountry && !selectedDiscipline && (
            <p className="text-xs text-gray-500 italic">
              Please select a country first
            </p>
          )}
          {selectedDiscipline && (
            <p className="text-xs text-gray-500 italic">
              Country selection is disabled when a discipline is selected
            </p>
          )}
        </div>

        {/* Discipline Selector */}
        <div className="space-y-1.5">
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide flex items-center">
            <FaBook className="text-[#DB4F40] mr-1.5 w-3 h-3" />
            Discipline
          </h3>

          <ReactSelect
            options={[
              { value: '', label: 'All Disciplines' },
              ...disciplines.map(discipline => ({
                value: discipline.ai_department,
                label: `${discipline.ai_department} (${discipline.Count})`
              }))
            ]}
            value={{
              value: selectedDiscipline,
              label: selectedDiscipline
                ? `${selectedDiscipline} (${disciplines.find(d => d.ai_department === selectedDiscipline)?.Count || 0})`
                : 'All Disciplines'
            }}
            onChange={(selectedOption) => handleDisciplineChange({
              target: { value: selectedOption.value }
            })}
            isSearchable
            isDisabled={!!selectedCountry}
            placeholder={selectedCountry ? "Disabled when country is selected" : "Search disciplines..."}
            className="react-select-container"
            classNamePrefix="react-select"
            styles={{
              control: (base) => ({
                ...base,
                border: '1px solid #E5E7EB',
                borderRadius: '6px',
                padding: '2px 6px',
                minHeight: '36px',
                fontSize: '14px',
                '&:hover': { borderColor: '#DB4F40' },
                '&:focus-within': {
                  borderColor: '#DB4F40',
                  boxShadow: '0 0 0 3px rgba(219, 79, 64, 0.1)'
                },
                opacity: selectedCountry ? 0.6 : 1
              }),
              option: (base, { isFocused }) => ({
                ...base,
                backgroundColor: isFocused ? '#FEF2F2' : 'white',
                color: '#374151',
                fontSize: '14px',
                padding: '6px 8px',
                '&:hover': { backgroundColor: '#FEF2F2' }
              }),
              menu: (base) => ({
                ...base,
                borderRadius: '6px',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
              }),
              placeholder: (base) => ({
                ...base,
                fontSize: '14px',
                color: '#9CA3AF'
              }),
              singleValue: (base) => ({
                ...base,
                fontSize: '14px'
              }),
              input: (base) => ({
                ...base,
                fontSize: '14px'
              })
            }}
          />
          {selectedCountry && (
            <p className="text-xs text-gray-500 italic">
              Discipline selection is disabled when a country is selected
            </p>
          )}
        </div>

        {/* Date Range Selector */}
        <div className="space-y-1.5">
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide flex items-center">
            <FaClock className="text-[#DB4F40] mr-1.5 w-3 h-3" />
            Published Date
          </h3>
          <div className="space-y-1.5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <DatePicker
                selected={startDate}
                onChange={handleStartDateChange}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                placeholderText="Select start date"
                className="w-full p-1.5 text-sm border border-gray-300 rounded-md focus:border-[#DB4F40] focus:ring-1 focus:ring-[#DB4F40] focus:ring-opacity-20 transition-colors"
                isClearable
                wrapperClassName="w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <DatePicker
                selected={endDate}
                onChange={handleEndDateChange}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                placeholderText="Select end date"
                className="w-full p-1.5 text-sm border border-gray-300 rounded-md focus:border-[#DB4F40] focus:ring-1 focus:ring-[#DB4F40] focus:ring-opacity-20 transition-colors"
                isClearable
                wrapperClassName="w-full"
              />
            </div>
          </div>
        </div>

        {/* Quick Filters */}
        <div className="space-y-1.5">
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
            Quick Filters
          </h3>
          <div className="space-y-1.5">
            <label className="flex items-center p-2 bg-gray-50 rounded-md border border-gray-200 hover:border-[#DB4F40] transition-colors cursor-pointer">
              <input
                type="checkbox"
                checked={showFullyFundedOnly}
                onChange={() => handleCheckboxChange(setShowFullyFundedOnly, 'Fully Funded')}
                className="h-3 w-3 text-[#DB4F40] border-gray-300 rounded focus:ring-[#DB4F40] focus:ring-1"
              />
              <div className="ml-2">
                <p className="text-sm font-medium text-gray-900">Fully Funded Only</p>
                <p className="text-sm text-gray-500">Minimal financial barriers</p>
              </div>
            </label>
          </div>
        </div>

        {/* Clear Filters Button */}
        <button
          onClick={handleClearFilters}
          className="w-full px-3 py-2 bg-gradient-to-r from-[#DB4F40] to-[#C64537] text-white rounded-md hover:from-[#C64537] hover:to-[#DB4F40] transition-all duration-200 shadow-sm hover:shadow-md text-sm font-medium"
        >
          Clear All Filters
        </button>
      </div>
    </div>
  );
};

export default OpportunityFilters; 